import { useEffect, useState } from 'react';
import {
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

type UpdatePhase = 'updating' | 'restarting' | 'done' | 'timeout';

export function UpdateDialog({ open, onDone }: { open: boolean; onDone: () => void }) {
  const [phase, setPhase] = useState<UpdatePhase>('updating');

  useEffect(() => {
    if (!open) return;
    setPhase('updating');
    let wentDown = false;
    let stopped = false;

    const poll = setInterval(async () => {
      if (stopped) return;
      try {
        const res = await fetch('/api/health');
        if (res.ok && wentDown) {
          stopped = true;
          clearInterval(poll);
          clearTimeout(giveUp);
          setPhase('done');
          setTimeout(onDone, 1500);
        }
      } catch {
        if (!wentDown) { wentDown = true; setPhase('restarting'); }
      }
    }, 3_000);

    const giveUp = setTimeout(() => {
      if (!stopped) { stopped = true; clearInterval(poll); setPhase('timeout'); }
    }, 8 * 60 * 1_000);

    return () => { stopped = true; clearInterval(poll); clearTimeout(giveUp); };
  }, [open, onDone]);

  return (
    <Dialog open={open} maxWidth="xs" fullWidth>
      <DialogTitle>
        {phase === 'done' ? 'Update complete' : phase === 'timeout' ? 'Update taking too long' : 'Update in progress'}
      </DialogTitle>
      <DialogContent>
        <Stack alignItems="center" spacing={2} py={2}>
          {phase === 'updating' && (
            <>
              <CircularProgress size={48} />
              <Typography variant="body2" color="text.secondary" textAlign="center">
                Pulling latest code and rebuilding — this takes <strong>2–5 minutes</strong>.
              </Typography>
              <Typography variant="caption" color="text.secondary" textAlign="center">
                The page will reload automatically once the service is back online.
              </Typography>
            </>
          )}
          {phase === 'restarting' && (
            <>
              <CircularProgress size={48} color="warning" />
              <Typography variant="body2" color="text.secondary" textAlign="center">Build complete — service is restarting…</Typography>
            </>
          )}
          {phase === 'done' && (
            <>
              <CheckCircleIcon color="success" sx={{ fontSize: 48 }} />
              <Typography variant="body2" color="text.secondary">Service is back online. Reloading…</Typography>
            </>
          )}
          {phase === 'timeout' && (
            <>
              <Typography variant="body2" color="error" textAlign="center">The update is taking longer than expected. Check the server logs:</Typography>
              <Typography variant="caption" sx={{ fontFamily: 'monospace', bgcolor: 'background.paper', px: 1.5, py: 0.5, borderRadius: 1 }}>
                sudo journalctl -u webadmin -f
              </Typography>
            </>
          )}
        </Stack>
      </DialogContent>
    </Dialog>
  );
}

export function RestartDialog({ open, onDone }: { open: boolean; onDone: () => void }) {
  const [phase, setPhase] = useState<UpdatePhase>('updating');

  useEffect(() => {
    if (!open) return;
    setPhase('updating');
    let wentDown = false;
    let stopped = false;

    const poll = setInterval(async () => {
      if (stopped) return;
      try {
        const res = await fetch('/api/health');
        if (res.ok && wentDown) {
          stopped = true;
          clearInterval(poll);
          clearTimeout(giveUp);
          setPhase('done');
          setTimeout(onDone, 1000);
        }
      } catch {
        if (!wentDown) { wentDown = true; setPhase('restarting'); }
      }
    }, 1_500);

    const giveUp = setTimeout(() => {
      if (!stopped) { stopped = true; clearInterval(poll); setPhase('timeout'); }
    }, 2 * 60 * 1_000);

    return () => { stopped = true; clearInterval(poll); clearTimeout(giveUp); };
  }, [open, onDone]);

  return (
    <Dialog open={open} maxWidth="xs" fullWidth>
      <DialogTitle>
        {phase === 'done' ? 'Restart complete' : phase === 'timeout' ? 'Restart taking too long' : 'Restarting service'}
      </DialogTitle>
      <DialogContent>
        <Stack alignItems="center" spacing={2} py={2}>
          {(phase === 'updating' || phase === 'restarting') && (
            <>
              <CircularProgress size={48} color={phase === 'restarting' ? 'warning' : 'primary'} />
              <Typography variant="body2" color="text.secondary" textAlign="center">
                {phase === 'updating' ? 'Restarting webadmin service…' : 'Service is coming back online…'}
              </Typography>
            </>
          )}
          {phase === 'done' && (
            <>
              <CheckCircleIcon color="success" sx={{ fontSize: 48 }} />
              <Typography variant="body2" color="text.secondary">Service restarted. Reloading…</Typography>
            </>
          )}
          {phase === 'timeout' && (
            <Typography variant="body2" color="error" textAlign="center">
              Restart is taking longer than expected. Check the server logs.
            </Typography>
          )}
        </Stack>
      </DialogContent>
    </Dialog>
  );
}
